"use client";

import { useState } from "react";
import { ArrowRight, ArrowUpRight, FileText, Megaphone } from "lucide-react";
import type {
	InformationHubComponent,
	InformationHubCommunique,
	InformationHubLatestVideos,
	InformationHubPublicationItem,
	InformationHubTab,
	InformationHubVideoCard,
	LinkTarget,
	PaginatedActivityGridItem,
} from "../../types";
import { DocumentCard } from "../../components/activities/DocumentCard";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { localizeFrenchDateString } from "../../utils/frenchDate";
import { resolveLinkHref } from "../../utils/links";
import {
	getImageObjectPositionStyle,
	getImageOverrideProps,
	mergeClassNames,
	resolveImageValue,
} from "../../utils/imageOptions";

/**
 * Information hub: home-page replacement for the legacy "publications +
 * communiqués" Drupal block. Two columns, fully data-driven:
 *
 *   LEFT  — `PublicationsTabsCard`. Single white card with a wrapping pill
 *           tab bar and a divided list below. Active tab tracked client-side.
 *   RIGHT — `CommuniquesColumn`. Stacked communiqué cards, each with a left
 *           date badge, document icon, title, date/time line, and a
 *           "Lire la suite" CTA pill.
 *
 * Tabs, items, communiqués and CTAs all come from `component.content`. An
 * optional `backgroundImageSrc` reuses the desaturated watermark frame from
 * `LatestArticleGrid` for a soft brand backdrop. */
export function InformationHubRenderer({ component }: { component: InformationHubComponent }) {
	const locale = useLocale();
	const {
		title,
		publicationsTitle,
		communiquesTitle,
		tabs,
		communiques,
		ctaLabel,
		ctaHref,
		communiquesViewAllLabel,
		communiquesViewAllHref,
		backgroundImageSrc,
		latestVideos,
	} = component.content;
	const hasBackground = Boolean(backgroundImageSrc);

	const inner = (
		<div className="relative mx-auto max-w-7xl px-6 md:px-8">
			{title ? (
				<header className="mb-6 md:mb-8">
					<h3 className="text-[28px] font-bold tracking-tight text-[#00B862] md:text-[32px]">{title}</h3>
					<div className="mt-2 h-1 w-12 rounded-sm bg-[#FFC105]" aria-hidden="true" />
				</header>
			) : null}
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
				<PublicationsTabsCard
					tabs={tabs ?? []}
					smallLabel={publicationsTitle}
					defaultCtaLabel={ctaLabel}
					defaultCtaHref={ctaHref}
				/>
				<CommuniquesColumn
					title={communiquesTitle ?? t(locale, "pressReleases")}
					items={communiques ?? []}
					viewAllLabel={communiquesViewAllLabel}
					viewAllHref={communiquesViewAllHref}
				/>
			</div>
			<LatestVideosRow videos={latestVideos} />
		</div>
	);

	if (hasBackground) {
		return <BackgroundFrame imageSrc={backgroundImageSrc!}>{inner}</BackgroundFrame>;
	}
	return <section className="py-10">{inner}</section>;
}

/* -------------------------------------------------------------------------- */
/* "Dernières vidéos" row                                                     */
/* -------------------------------------------------------------------------- */

/** Per-card responsive visibility so the video row is ALWAYS one line:
 *  1 card on mobile, 2 on tablet (md+), 3 on desktop (lg+). The grid is
 *  `grid-cols-1 md:grid-cols-2 lg:grid-cols-3`; cards past the breakpoint's
 *  column count are `hidden`. Index 3+ never shows (maxItems caps the row at 3). */
const LATEST_VIDEOS_ROW_VISIBILITY = ["block", "hidden md:block", "hidden lg:block"];

/** A third block INSIDE the Centre d'information section: a "Dernières vidéos"
 *  heading styled exactly like the section title (brand-blue + the yellow accent
 *  rule) followed by a 3-up grid of the SAME `/actualites/videos` cards
 *  (`DocumentCard`). The `videos.items` are resolved at request time from the
 *  `latest-videos` fragment, so a newly published video shows up automatically.
 *  Renders nothing when there are no videos. */
function LatestVideosRow({ videos }: { videos?: InformationHubLatestVideos }) {
	const locale = useLocale();
	const items = videos?.items ?? [];
	if (items.length === 0) return null;
	const viewAllHref = resolveLinkHref(videos?.viewAllHref);

	return (
		<div className="mt-10 md:mt-12">
			<header className="mb-5 md:mb-6">
				<h3 className="text-[24px] font-bold tracking-tight text-[#00B862] md:text-[28px]">
					{t(locale, "latestVideos")}
				</h3>
				<div className="mt-2 h-1 w-12 rounded-sm bg-[#FFC105]" aria-hidden="true" />
			</header>
			<div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
				{items.map((video, index) => (
					// The row is ALWAYS a single line — 1 card on mobile, 2 on tablet, 3 on
					// desktop — so the home section never grows a second row of videos. Cards
					// past the breakpoint's column count are hidden (kept in the DOM, just not
					// shown) via `LATEST_VIDEOS_ROW_VISIBILITY`.
					<div
						key={`${video.title}-${index}`}
						className={`h-full ${LATEST_VIDEOS_ROW_VISIBILITY[index] ?? "hidden"}`}
					>
						<DocumentCard item={toVideoCardItem(video)} />
					</div>
				))}
			</div>
			{viewAllHref ? (
				<RuntimeLink
					href={viewAllHref}
					className="group mt-5 inline-flex items-center gap-1.5 text-[14px] xl:text-[16px] font-semibold text-[#00A356] transition hover:text-[#00B862]"
				>
					{t(locale, "allVideos")}
					<ArrowRight className="h-4 w-4 text-[#FFC105] transition group-hover:translate-x-0.5" />
				</RuntimeLink>
			) : null}
		</div>
	);
}

/** Map a fragment video card onto the shared `DocumentCard` shape: the
 *  pre-formatted `date` becomes the card's meta line and `excerpt` its optional
 *  description (videos carry none today, so the card reads thumbnail · date ·
 *  title · "Regarder"). */
function toVideoCardItem(video: InformationHubVideoCard): PaginatedActivityGridItem {
	return {
		title: video.title,
		// `href` is required on the card type; a video always carries one, but fall
		// back to an empty target (DocumentCard then renders a non-link card).
		href: video.href ?? { slug: "" },
		imageSrc: video.imageSrc,
		meta: video.date,
		description: video.excerpt,
	};
}

/* -------------------------------------------------------------------------- */
/* Publications tabs card                                                     */
/* -------------------------------------------------------------------------- */

function PublicationsTabsCard({
	tabs,
	smallLabel,
	defaultCtaLabel,
	defaultCtaHref,
}: {
	tabs: InformationHubTab[];
	smallLabel?: string;
	defaultCtaLabel?: string;
	defaultCtaHref?: LinkTarget;
}) {
	const locale = useLocale();
	const [activeId, setActiveId] = useState<string | undefined>(tabs[0]?.id);
	const active = tabs.find((tab) => tab.id === activeId) ?? tabs[0];
	if (!active) return null;

	// Pick the per-tab "voir tout" if present; otherwise fall back to the
	// section-level CTA. Always render exactly ONE bottom link — the previous
	// version showed two identical CTAs side-by-side.
	const tabHref = resolveLinkHref(active.viewAllHref) ?? resolveLinkHref(defaultCtaHref);
	const tabLabel = active.viewAllLabel ?? defaultCtaLabel;

	return (
		<div className="flex h-full flex-col overflow-hidden rounded-md bg-white shadow-[var(--shadow-e1)] ring-1 ring-slate-200/80">
			{smallLabel ? (
				<div className="px-5 pt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#34D98A]">
					{smallLabel}
				</div>
			) : null}
			<div className="px-3 pt-4 pb-3">
				<div role="tablist" aria-label="Publications" className="flex flex-wrap gap-1.5">
					{tabs.map((tab) => {
						const selected = tab.id === active.id;
						return (
							<button
								key={tab.id}
								type="button"
								role="tab"
								aria-selected={selected}
								onClick={() => setActiveId(tab.id)}
								className={
									"inline-flex items-center rounded-sm px-3.5 py-1.5 text-[13px] xl:text-[15px] font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A356]/40 " +
									(selected
										? "bg-[#00B862] text-white shadow-sm"
										: "bg-slate-100 text-slate-600 hover:bg-slate-200/70 hover:text-slate-900")
								}
							>
								{tab.label}
							</button>
						);
					})}
				</div>
			</div>
			<ul className="flex-1 divide-y divide-slate-100 px-1">
				{active.items.length === 0 ? (
					<li className="px-4 py-8 text-center text-sm xl:text-base text-slate-500">
						{t(locale, "noRecentPublications")}
					</li>
				) : (
					active.items.map((item, idx) => <PublicationRow key={`${active.id}-${idx}`} item={item} />)
				)}
			</ul>
			{tabHref && tabLabel ? (
				<div className="border-t border-slate-100 px-5 py-3.5">
					<RuntimeLink
						href={tabHref}
						className="group inline-flex items-center gap-1.5 text-[14px] xl:text-[16px] font-semibold text-[#00A356] transition hover:text-[#00B862]"
					>
						{tabLabel}
						<ArrowRight className="h-4 w-4 text-[#FFC105] transition group-hover:translate-x-0.5" />
					</RuntimeLink>
				</div>
			) : null}
		</div>
	);
}

function PublicationRow({ item }: { item: InformationHubPublicationItem }) {
	const locale = useLocale();
	const href = resolveLinkHref(item.href);
	const body = (
		<div className="flex items-start gap-3 rounded-sm px-3 py-3 transition group-hover:bg-slate-50">
			<div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-sm bg-[#E6F4EA] text-[#00B862]">
				<FileText className="h-3.5 w-3.5" aria-hidden="true" />
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-center gap-x-1.5 text-[12px] xl:text-[14px] text-slate-500">
					<span className="font-medium text-slate-600">{item.type}</span>
					{item.date ? (
						<>
							<span className="text-slate-300">•</span>
							<span>{localizeFrenchDateString(item.date, locale)}</span>
						</>
					) : null}
				</div>
				<div className="mt-1 text-[15px] md:text-[16px] xl:text-[18px] font-semibold leading-snug text-[#00B862] group-hover:underline">
					{item.title}
				</div>
			</div>
			<ArrowUpRight
				className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-[#FFC105]"
				aria-hidden="true"
			/>
		</div>
	);

	return (
		<li>
			{href ? (
				<RuntimeLink href={href} className="group block">
					{body}
				</RuntimeLink>
			) : (
				<div className="group">{body}</div>
			)}
		</li>
	);
}

/* -------------------------------------------------------------------------- */
/* Communiqués column                                                         */
/* -------------------------------------------------------------------------- */

function CommuniquesColumn({
	title,
	items,
	viewAllLabel,
	viewAllHref,
}: {
	title: string;
	items: InformationHubCommunique[];
	viewAllLabel?: string;
	viewAllHref?: LinkTarget;
}) {
	const resolvedViewAll = resolveLinkHref(viewAllHref);
	return (
		<div className="flex h-full flex-col">
			<div className="mb-4 flex items-center gap-2.5">
				<div className="grid h-9 w-9 place-items-center rounded-sm bg-[#00B862] text-white shadow-sm">
					<Megaphone className="h-4 w-4" aria-hidden="true" />
				</div>
				<h4 className="text-[20px] font-bold text-[#00B862] md:text-[22px]">{title}</h4>
			</div>
			<ul className="flex flex-col gap-3.5">
				{items.map((c, idx) => (
					<li key={`${c.title}-${idx}`}>
						<CommuniqueCard item={c} />
					</li>
				))}
			</ul>
			{resolvedViewAll && viewAllLabel ? (
				<RuntimeLink
					className="group mt-5 inline-flex items-center gap-1.5 self-start text-[14px] xl:text-[16px] font-semibold text-[#00A356] transition hover:text-[#00B862]"
					href={resolvedViewAll}
				>
					{viewAllLabel}
					<ArrowRight className="h-4 w-4 text-[#FFC105] transition group-hover:translate-x-0.5" />
				</RuntimeLink>
			) : null}
		</div>
	);
}

function CommuniqueCard({ item }: { item: InformationHubCommunique }) {
	const locale = useLocale();
	const href = resolveLinkHref(item.href);
	const ctaLabel = item.ctaLabel ?? t(locale, "readMore");
	const badge = resolveBadge(item, locale);

	const inner = (
		<article className="relative flex gap-4 overflow-hidden rounded-md bg-white p-5 shadow-[var(--shadow-e1)] ring-1 ring-slate-200/80 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:shadow-[var(--shadow-e2)] group-hover:ring-[#34D98A]/70">
			<DateBadge day={badge.day} month={badge.month} />
			<div className="flex min-w-0 flex-1 flex-col">
				<h5 className="text-[15px] md:text-[16px] xl:text-[18px] font-bold leading-snug text-[#00B862]">
					{item.title}
				</h5>
				<div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[12px] xl:text-[14px] text-slate-500">
					<span>{localizeFrenchDateString(item.date, locale)}</span>
					{item.time ? (
						<>
							<span className="text-slate-300">•</span>
							<span>{item.time}</span>
						</>
					) : null}
				</div>
				{href ? (
					<div className="mt-3">
						<span className="inline-flex items-center gap-2 rounded-sm bg-[#FFC105] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-white shadow-sm transition group-hover:bg-[#E0A800]">
							{ctaLabel}
							<ArrowRight className="h-3.5 w-3.5 text-white" />
						</span>
					</div>
				) : null}
			</div>
		</article>
	);

	return href ? (
		<RuntimeLink href={href} className="group block">
			{inner}
		</RuntimeLink>
	) : (
		<div className="group">{inner}</div>
	);
}

/** Square date badge — bigger, bolder, and using the brand palette. The day
 *  is yellow on the dark blue ground, the month line is small and
 *  subdued. Same design language as the publication-icon chip. Sized (and
 *  side-padded) so the longest localized month line ("sept, 26") sits on one
 *  line with margins, never edge-to-edge or wrapped. */
function DateBadge({ day, month }: { day: string; month: string }) {
	return (
		<div className="flex h-[82px] w-[80px] shrink-0 flex-col items-center justify-center rounded-sm bg-gradient-to-b from-[#00A356] to-[#00B862] px-2 text-white shadow-sm ring-1 ring-white/10">
			<span className="text-[26px] font-extrabold leading-none text-[#FFC105]">{day || "—"}</span>
			<span className="mt-1.5 whitespace-nowrap text-[12px] font-semibold uppercase tracking-[0.06em] text-white/85">
				{month || ""}
			</span>
		</div>
	);
}

/** Resolve the day/month for the date badge. Author-provided values win;
 *  otherwise we parse a small set of common French date strings. */
function resolveBadge(item: InformationHubCommunique, locale: string): { day: string; month: string } {
	if (item.badgeDay && item.badgeMonth) {
		return { day: item.badgeDay, month: item.badgeMonth };
	}
	const fromDate = parseFrenchDate(item.date, locale);
	return {
		day: item.badgeDay ?? fromDate.day,
		month: item.badgeMonth ?? fromDate.month,
	};
}

const FR_MONTHS: Record<string, string> = {
	janvier: "jan",
	février: "fév",
	mars: "mars",
	avril: "avr",
	mai: "mai",
	juin: "juin",
	juillet: "juil",
	août: "août",
	septembre: "sept",
	octobre: "oct",
	novembre: "nov",
	décembre: "déc",
};

/** Locale-aware month abbreviation for the date badge. Date PARSING stays
 *  French (the CMS emits French date strings on every locale variant); only
 *  the DISPLAYED abbreviation follows the locale. An unknown month or an
 *  invalid locale tag falls back to the French abbreviation. */
function localizedMonthAbbrev(locale: string, year: number, monthIndex: number, frenchAbbrev: string): string {
	if (!locale || locale === "fr" || monthIndex < 0 || monthIndex > 11) return frenchAbbrev;
	try {
		return new Intl.DateTimeFormat(locale === "en" ? "en-GB" : locale, { month: "short" }).format(
			new Date(Date.UTC(year, monthIndex, 1)),
		);
	} catch {
		return frenchAbbrev;
	}
}

function parseFrenchDate(input: string | undefined, locale: string): { day: string; month: string } {
	if (!input) return { day: "", month: "" };
	const word = input.match(/(\d{1,2})\s+([a-zéûôîâ]+)\s+(\d{4})/i);
	if (word) {
		const day = word[1].padStart(2, "0");
		const monthKey = word[2].toLowerCase();
		const frenchShort = FR_MONTHS[monthKey] ?? monthKey.slice(0, 3);
		const monthIndex = Object.keys(FR_MONTHS).indexOf(monthKey);
		const monthShort = localizedMonthAbbrev(locale, Number(word[3]), monthIndex, frenchShort);
		const yearShort = word[3].slice(2);
		return { day, month: `${monthShort}, ${yearShort}` };
	}
	const numeric = input.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
	if (numeric) {
		const day = numeric[1].padStart(2, "0");
		const idx = Number(numeric[2]) - 1;
		const frenchName = Object.values(FR_MONTHS)[idx] ?? "";
		const monthName = localizedMonthAbbrev(locale, Number(numeric[3]), idx, frenchName);
		const year = numeric[3].slice(-2);
		return { day, month: `${monthName}, ${year}` };
	}
	return { day: "", month: "" };
}

/* -------------------------------------------------------------------------- */
/* Background watermark frame (shared visual with LatestArticleGrid)          */
/* -------------------------------------------------------------------------- */

/** Same isolation/overlay treatment used by `LatestArticleGrid` and
 *  `PublicationsUpdates`. Image is desaturated and a 0.92 white wash sits on
 *  top — light enough to keep the watermark texture readable while keeping
 *  the cards as the primary content. */
function BackgroundFrame({
	imageSrc,
	children,
}: {
	imageSrc: NonNullable<InformationHubComponent["content"]["backgroundImageSrc"]>;
	children: React.ReactNode;
}) {
	const { src, options } = resolveImageValue(imageSrc);
	const backgroundSrc = src || "/media/welcome_background.jpg";
	const objectPositionStyle = getImageObjectPositionStyle(options);
	const overrideProps = getImageOverrideProps(options);

	return (
		<section
			className={mergeClassNames(
				"relative z-0 isolate -mt-6 mx-[calc(50%-50vw)]",
				overrideProps.className
			)}
			style={overrideProps.style}
		>
			<div className="absolute inset-0" aria-hidden="true">
				<div className="relative h-full w-full">
					<RuntimeImage
						src={backgroundSrc}
						alt=""
						className="absolute inset-0 h-full w-full object-cover object-center"
						style={{ ...objectPositionStyle, filter: "grayscale(1)" }}
					/>
				</div>
				<div className="absolute inset-0" style={{ background: "lab(100 0 0 / 0.9)" }} aria-hidden="true" />
			</div>
			<div className="relative py-14">{children}</div>
		</section>
	);
}
